import { Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Sparkles, LayoutDashboard, Users, Gift, Settings, LogOut,
  Zap, Tablet, CheckCircle2, Plus, Trash2, Search,
  CreditCard, AlertTriangle, Megaphone, Eye, EyeOff, ImageIcon, Pencil,
  MoreVertical, Minus, BarChart3, TrendingUp, DollarSign, Upload, FileText,
  MessageSquare, Send,
} from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";
import { useKioskLock } from "@/hooks/useKioskLock";
import { useLongPress } from "@/hooks/useLongPress";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import SupportChatWidget from "@/components/SupportChatWidget";
import BillingConfirm from "@/pages/BillingConfirm";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancelled from "@/pages/BillingCancelled";

const POLL_MS = 2000;

// Display a stored US phone (11 digits starting with '1') on the merchant
// portal as flat "15162051887" — leading '1', no '+', no formatting.
function fmtPhone(raw) {
  const d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return "1" + d;
  return d;
}

export default function MerchantApp() {
  const { user, business, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSession, setActiveSession] = useState(null);
  const [billing, setBilling] = useState(null);
  const { locked, lock, unlock } = useKioskLock();
  const [unlockPromptOpen, setUnlockPromptOpen] = useState(false);
  const { progress: unlockProgress, handlers: unlockHoldHandlers } = useLongPress({
    duration: 5000,
    enabled: locked,
    onComplete: () => setUnlockPromptOpen(true),
  });

  useHideEmergentBadge();

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/business/billing");
      setBilling(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!business) return;
    refreshBilling();
  }, [business, refreshBilling]);

  // After Stripe Checkout, the customer returns to the clean URL
  // /merchant/billing/success (no session token in the URL). The
  // BillingReturnPage component handles the sync + status polling.

  // Poll for active kiosk session
  useEffect(() => {
    if (!business) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/business/kiosk-session");
        setActiveSession(data.active ? data : null);
      } catch {
        // ignore
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [business]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const openKiosk = () => {
    if (!business) return;
    window.open(`${window.location.origin}/kiosk/${business.slug}`, "_blank");
  };

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get("/business/kiosk-session");
      setActiveSession(data.active ? data : null);
    } catch {}
  }, []);

  const clearSession = useCallback(async () => {
    try {
      await api.post("/business/kiosk-session/done");
      setActiveSession(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb]">
        <div className="text-sm uppercase tracking-[0.2em] text-[#002fa7] font-semibold">Loading…</div>
      </div>
    );
  }

  const navItems = [
    { to: "/merchant", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/merchant/customers", label: "Customers", icon: Users },
    { to: "/merchant/rewards", label: "Rewards", icon: Gift },
    { to: "/merchant/promotions", label: "Promotions", icon: Megaphone },
    { to: "/merchant/marketing", label: "SMS Marketing", icon: MessageSquare },
    { to: "/merchant/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/merchant/settings", label: "Settings", icon: Settings },
  ];

  const pastDue = billing?.status === "past_due";

  return (
    <div className="min-h-screen bg-[#f9f9fb] flex">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#e5e5eb] sticky top-0 h-screen">
        <div className="p-6 border-b border-[#e5e5eb]">
          <button
            type="button"
            data-testid="kiosk-lock-logo"
            {...unlockHoldHandlers}
            className="flex items-center gap-2 mb-6 w-full text-left select-none relative touch-none"
            style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
          >
            <div className="relative w-7 h-7">
              <div className="absolute inset-0 rounded-lg bg-[#002fa7] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              {locked && unlockProgress > 0 && (
                <svg
                  className="absolute -inset-1 pointer-events-none"
                  viewBox="0 0 36 36"
                  data-testid="kiosk-unlock-progress"
                >
                  <circle
                    cx="18" cy="18" r="16"
                    fill="none" stroke="#002fa7" strokeWidth="2.5"
                    strokeDasharray={`${unlockProgress * 100.53} 100.53`}
                    strokeDashoffset="0"
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
              )}
            </div>
            <span className="font-display font-extrabold text-lg">SparkLoop</span>
            {locked && unlockProgress > 0 && (
              <span className="ml-auto text-[10px] uppercase tracking-[0.12em] text-[#002fa7] font-bold">
                Hold… {Math.ceil((1 - unlockProgress) * 5)}s
              </span>
            )}
          </button>
          <div className="text-xs uppercase tracking-[0.16em] text-[#5a5a65] mb-1">Business</div>
          <div className="font-display font-bold text-lg leading-tight truncate">{business.name}</div>
          <div className="text-xs text-[#5a5a65] mt-1">{user?.name}</div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`sidebar-${item.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-[#002fa7] text-white" : "text-[#5a5a65] hover:text-[#0a0a0a] hover:bg-[#f0f0f4]"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#e5e5eb] space-y-2">
          <Button
            data-testid="sidebar-kiosk-btn"
            variant="outline"
            onClick={openKiosk}
            className="w-full rounded-xl border-[#e5e5eb] hover:border-[#0a0a0a]"
          >
            <Tablet className="w-4 h-4 mr-2" /> Open kiosk
          </Button>
          <button
            data-testid="sidebar-logout-btn"
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5a5a65] hover:text-[#0a0a0a] rounded-xl hover:bg-[#f0f0f4] ${locked ? "hidden" : ""}`}
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          {locked && (
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#5a5a65] text-center font-semibold py-2">
              🔒 Kiosk locked
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#e5e5eb] px-4 h-14 flex items-center justify-between">
          <button
            type="button"
            data-testid="kiosk-lock-logo-mobile"
            {...unlockHoldHandlers}
            className="flex items-center gap-2 select-none touch-none relative"
            style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
          >
            <div className="relative w-6 h-6">
              <div className="absolute inset-0 rounded bg-[#002fa7] flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
              {locked && unlockProgress > 0 && (
                <svg className="absolute -inset-1 pointer-events-none" viewBox="0 0 36 36">
                  <circle
                    cx="18" cy="18" r="16"
                    fill="none" stroke="#002fa7" strokeWidth="3"
                    strokeDasharray={`${unlockProgress * 100.53} 100.53`}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
              )}
            </div>
            <span className="font-display font-bold">{business.name}</span>
            {locked && unlockProgress > 0 && (
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#002fa7] font-bold">
                {Math.ceil((1 - unlockProgress) * 5)}s
              </span>
            )}
          </button>
          {!locked && (
            <button onClick={handleLogout} className="text-sm text-[#5a5a65]">
              <LogOut className="w-4 h-4" />
            </button>
          )}
          {locked && <span className="text-xs text-[#5a5a65]">🔒</span>}
        </div>

        <div className="md:hidden sticky top-14 z-30 bg-white border-b border-[#e5e5eb] px-2 overflow-x-auto">
          <div className="flex gap-1 py-2">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${
                    isActive ? "bg-[#002fa7] text-white" : "text-[#5a5a65]"
                  }`}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>

        {/* Billing banner — renewal soon / grace period / past due */}
        {billing && (
          billing.status === "past_due" ||
          billing.in_grace ||
          ((billing.status === "trialing" || billing.status === "active") && (billing.days_left ?? 99) <= 7)
        ) && (
          <BillingBanner billing={billing} />
        )}

        {/* Active kiosk session banner — shown at top of all merchant pages */}
        {activeSession && (
          <ActiveSessionBar
            session={activeSession}
            business={business}
            onAction={refreshSession}
            onDone={clearSession}
          />
        )}

        <Routes>
          <Route
            index
            element={
              pastDue
                ? <Navigate to="/merchant/settings" replace />
                : <MerchantDashboard business={business} activeSession={activeSession} onOpenKiosk={openKiosk} />
            }
          />
          <Route
            path="customers"
            element={
              pastDue
                ? <Navigate to="/merchant/settings" replace />
                : <MerchantCustomers business={business} activeSession={activeSession} onAction={refreshSession} />
            }
          />
          <Route
            path="rewards"
            element={pastDue ? <Navigate to="/merchant/settings" replace /> : <MerchantRewards />}
          />
          <Route
            path="promotions"
            element={pastDue ? <Navigate to="/merchant/settings" replace /> : <MerchantPromotions business={business} />}
          />
          <Route
            path="marketing"
            element={pastDue ? <Navigate to="/merchant/settings" replace /> : <MerchantMarketing business={business} />}
          />
          <Route
            path="analytics"
            element={pastDue ? <Navigate to="/merchant/settings" replace /> : <MerchantAnalytics />}
          />
          <Route path="settings" element={<MerchantSettings business={business} user={user} billing={billing} onRefreshBilling={refreshBilling} />} />
          <Route path="billing/confirm" element={<BillingConfirm />} />
          <Route path="billing/success" element={<BillingSuccess />} />
          <Route path="billing/cancelled" element={<BillingCancelled />} />
          <Route path="*" element={<Navigate to="/merchant" replace />} />
        </Routes>
      </main>
      <SupportChatWidget />
      <KioskUnlockDialog
        open={unlockPromptOpen}
        onClose={() => setUnlockPromptOpen(false)}
        onUnlocked={() => { unlock(); setUnlockPromptOpen(false); toast.success("Kiosk unlocked"); }}
      />
    </div>
  );
}

function KioskUnlockDialog({ open, onClose, onUnlocked }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setPw(""); }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!pw) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-admin-password", { password: pw });
      if (data?.ok) {
        onUnlocked();
      } else {
        toast.error("Wrong admin password");
      }
    } catch {
      toast.error("Couldn't verify — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Unlock kiosk</DialogTitle>
          <DialogDescription>
            Enter the SparkLoop admin password to exit kiosk mode on this device.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid="kiosk-unlock-password"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Admin password"
            autoFocus
          />
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} type="button" className="rounded-full">Cancel</Button>
            <Button data-testid="kiosk-unlock-submit" type="submit" disabled={busy || !pw} className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white">
              {busy ? "Checking…" : "Unlock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Active session bar (always visible at top when a customer is at kiosk) ----------------
function ActiveSessionBar({ session, business, onAction, onDone }) {
  const [mode, setMode] = useState("amount"); // "amount" | "custom"
  const [amount, setAmount] = useState("");
  const [customPoints, setCustomPoints] = useState("");
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);

  const approveRequested = async () => {
    if (!session.requested_reward) return;
    setApproving(true);
    try {
      await api.post("/business/redeem", {
        customer_id: session.customer.id,
        reward_id: session.requested_reward.id,
      });
      toast.success(`Redeemed: ${session.requested_reward.title}`);
      await onAction?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApproving(false);
    }
  };

  const give = async () => {
    const body = { phone: session.customer.phone };
    if (mode === "amount") {
      const a = Number(amount);
      if (!a || a <= 0) return toast.error("Enter the purchase amount");
      body.amount = a;
    } else {
      const p = Number(customPoints);
      if (!p || p <= 0) return toast.error("Enter point amount");
      body.custom_points = p;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/business/check-in", body);
      toast.success(`+${data.points_awarded} pts — new balance ${data.new_balance}`);
      setAmount(""); setCustomPoints("");
      await onAction?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="sticky top-0 md:top-0 z-30 bg-gradient-to-r from-[#002fa7] to-[#0048ff] text-white border-b border-[#002fa7]" data-testid="active-session-bar">
      {session.requested_reward && (
        <div data-testid="merchant-reward-request" className="bg-[#ffce00] text-[#0a0a0a] px-4 py-3">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Gift className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
                  Customer wants to redeem
                </div>
                <div className="font-display text-lg font-extrabold truncate">
                  {session.requested_reward.title} · {session.requested_reward.points_cost} pts
                </div>
              </div>
            </div>
            <Button
              data-testid="approve-reward-btn"
              onClick={approveRequested}
              disabled={approving}
              className="bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-full font-semibold whitespace-nowrap"
            >
              {approving ? "Approving…" : `Approve & redeem`}
            </Button>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto p-5 md:p-7 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-shrink-0 flex items-center gap-4">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[11px] md:text-xs uppercase tracking-[0.24em] opacity-80 font-bold">
              At the counter
            </div>
            <div className="font-display text-3xl md:text-4xl font-extrabold leading-[0.95] tracking-tight">
              {session.customer.name || fmtPhone(session.customer.phone)}
            </div>
            <div className="text-sm md:text-base opacity-90 font-mono-data mt-1">
              <span className="font-bold">{session.points.toLocaleString()}</span> pts · {session.visits} visits
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2.5 lg:grid lg:grid-cols-[1fr_auto_auto_auto] lg:items-center">
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full bg-white/10 h-10">
              <TabsTrigger value="amount" className="data-[state=active]:bg-white data-[state=active]:text-[#002fa7] text-white/80 text-sm font-semibold">$ Amount</TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-white data-[state=active]:text-[#002fa7] text-white/80 text-sm font-semibold">Custom pts</TabsTrigger>
            </TabsList>
            <TabsContent value="amount" className="mt-2">
              <Input
                data-testid="bar-amount-input"
                type="number" step="0.01" min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`$ amount · ${business.points_per_dollar} pt per $1`}
                className="h-12 bg-white text-[#0a0a0a] rounded-xl border-0 text-base"
              />
            </TabsContent>
            <TabsContent value="custom" className="mt-2">
              <Input
                data-testid="bar-custom-input"
                type="number" min="1"
                value={customPoints}
                onChange={(e) => setCustomPoints(e.target.value)}
                placeholder="points to give"
                className="h-12 bg-white text-[#0a0a0a] rounded-xl border-0 text-base"
              />
            </TabsContent>
          </Tabs>
          <div className="grid grid-cols-3 gap-2 sm:gap-2.5 lg:contents">
            <Button
              data-testid="bar-give-btn"
              onClick={give}
              disabled={submitting}
              className="bg-white text-[#002fa7] hover:bg-white/90 rounded-xl h-12 px-2 sm:px-4 font-bold text-sm sm:text-base w-full lg:w-auto"
            >
              <Zap className="w-4 h-4 mr-1 sm:mr-1.5" /> Give
            </Button>
            <Button
              data-testid="bar-redeem-btn"
              onClick={() => setRedeemOpen(true)}
              variant="outline"
              className="bg-transparent border-white/40 text-white hover:bg-white/10 rounded-xl h-12 px-2 sm:px-4 font-semibold text-sm sm:text-base w-full lg:w-auto"
            >
              <Gift className="w-4 h-4 mr-1 sm:mr-1.5" /> Redeem
            </Button>
            <Button
              data-testid="bar-done-btn"
              onClick={onDone}
              variant="outline"
              className="bg-transparent border-white/40 text-white hover:bg-white/10 rounded-xl h-12 px-2 sm:px-4 font-semibold text-sm sm:text-base w-full lg:w-auto"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
      <RedeemDialog
        open={redeemOpen}
        onOpenChange={setRedeemOpen}
        customer={session.customer}
        customerPoints={session.points}
        onDone={async () => { await onAction?.(); setRedeemOpen(false); }}
      />
    </div>
  );
}

function RedeemDialog({ open, onOpenChange, customer, customerPoints, onDone }) {
  const [rewards, setRewards] = useState([]);
  const [mode, setMode] = useState("reward");
  const [rewardId, setRewardId] = useState("");
  const [customPoints, setCustomPoints] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/business/rewards");
        setRewards(data);
        setMode(data.length > 0 ? "reward" : "custom");
      } catch {}
    })();
  }, [open]);

  const submit = async () => {
    const body = { customer_id: customer.id };
    if (mode === "reward") {
      if (!rewardId) return toast.error("Pick a reward");
      body.reward_id = rewardId;
    } else {
      const p = Number(customPoints);
      if (!p || p <= 0) return toast.error("Enter point amount");
      if (p > customerPoints) return toast.error("Insufficient points");
      body.custom_points = p;
      body.custom_title = customTitle.trim() || "Custom redemption";
    }
    setSubmitting(true);
    try {
      await api.post("/business/redeem", body);
      toast.success("Redeemed");
      await onDone?.();
      setRewardId(""); setCustomPoints(""); setCustomTitle("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="redeem-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Redeem points</DialogTitle>
          <DialogDescription>
            {customer?.name || fmtPhone(customer?.phone)} · balance{" "}
            <span className="font-mono-data font-bold text-[#002fa7]">{customerPoints}</span> pts
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="reward" disabled={rewards.length === 0}>Saved reward</TabsTrigger>
            <TabsTrigger value="custom">Custom amount</TabsTrigger>
          </TabsList>
          <TabsContent value="reward" className="mt-4">
            <Select value={rewardId} onValueChange={setRewardId}>
              <SelectTrigger className="h-12 rounded-xl border-[#e5e5eb]"><SelectValue placeholder="Choose reward…" /></SelectTrigger>
              <SelectContent>
                {rewards.map((r) => (
                  <SelectItem key={r.id} value={r.id} disabled={r.points_cost > customerPoints}>
                    {r.title} · {r.points_cost} pts
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="custom" className="mt-4 space-y-3">
            <Input type="number" min="1" max={customerPoints}
              value={customPoints} onChange={(e) => setCustomPoints(e.target.value)}
              placeholder="Points to redeem"
              className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" />
            <Input value={customTitle} onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Label (optional) — e.g. $5 off drinks"
              className="h-12 rounded-xl border-[#e5e5eb]" />
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
          <Button data-testid="redeem-submit-btn" disabled={submitting} onClick={submit}
            className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">
            {submitting ? "Redeeming…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Dashboard ----------------
function MerchantDashboard({ business, activeSession, onOpenKiosk }) {
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/business/stats"),
        api.get("/business/transactions"),
      ]);
      setStats(s.data);
      setTxs(t.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [activeSession]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">Today's loop.</h1>
        </div>
        <Button
          data-testid="dashboard-kiosk-btn"
          onClick={onOpenKiosk}
          className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-6"
        >
          <Tablet className="w-4 h-4 mr-2" /> Open kiosk
        </Button>
      </div>

      {!activeSession && (
        <div className="bg-white border-dashed border-2 border-[#e5e5eb] rounded-2xl p-8 text-center mb-8">
          <div className="w-12 h-12 rounded-full bg-[#002fa7]/10 text-[#002fa7] flex items-center justify-center mx-auto mb-3">
            <Tablet className="w-6 h-6" />
          </div>
          <div className="font-display text-xl font-bold">No customer at the counter</div>
          <p className="text-sm text-[#5a5a65] mt-1">
            When a customer enters their phone on the kiosk, their card shows up here.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label="Customers" value={stats?.total_customers ?? "—"} accent="#002fa7" />
        <Stat label="Visits" value={stats?.total_visits ?? "—"} />
        <Stat label="Points issued" value={stats?.total_points_issued?.toLocaleString?.() ?? "—"} accent="#009e60" />
        <Stat label="Redemptions" value={stats?.redemptions ?? "—"} accent="#ffb800" />
      </div>

      <div className="bg-white border border-[#e5e5eb] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#5a5a65] font-semibold">Recent activity</div>
            <div className="font-display text-xl font-bold mt-1">Transactions</div>
          </div>
        </div>
        {txs.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#5a5a65]">No activity yet.</div>
        ) : (
          <div className="space-y-1 max-h-[360px] overflow-auto">
            {txs.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3 px-2 hover:bg-[#f9f9fb] rounded-xl">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.type === "check_in" ? "bg-[#002fa7]/10 text-[#002fa7]" : "bg-[#ffb800]/15 text-[#b38300]"}`}>
                    {t.type === "check_in" ? <Zap className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.customer_name || fmtPhone(t.customer_phone)}</div>
                    <div className="text-xs text-[#5a5a65]">
                      {t.type === "check_in" ? "Points awarded" : `Redeemed · ${t.reward_title || ""}`}
                    </div>
                  </div>
                </div>
                <div className={`font-mono-data text-sm font-bold ${t.points > 0 ? "text-[#009e60]" : "text-[#5a5a65]"}`}>
                  {t.points > 0 ? "+" : ""}{t.points}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-6 flex flex-col justify-between min-h-[140px]">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5a5a65]">{label}</div>
      <div className="font-display text-4xl font-extrabold tracking-tight mt-4" style={{ color: accent || "#0a0a0a" }}>
        {value}
      </div>
    </div>
  );
}

// ---------------- Customers ----------------
function MerchantCustomers({ business, activeSession, onAction }) {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [redeemFor, setRedeemFor] = useState(null);
  const [giveFor, setGiveFor] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/business/customers");
      setCustomers(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [activeSession]);

  const filtered = customers.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q) || c.phone.includes(q);
  });

  const removeCustomer = async (c) => {
    if (!window.confirm(`Remove ${c.name || fmtPhone(c.phone)} from your list? Their points and history with you will be deleted.`)) return;
    try {
      await api.delete(`/business/customers/${c.customer_id}`);
      toast.success("Customer removed");
      await load();
      await onAction?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">{customers.length} total</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">Your customers.</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65]" />
            <Input data-testid="customers-search-input" placeholder="Search name or phone"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="h-11 pl-11 rounded-full border-[#e5e5eb] bg-white" />
          </div>
          <Button
            data-testid="add-customer-btn"
            onClick={() => setAddOpen(true)}
            className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add customer
          </Button>
          <Button
            data-testid="import-customers-btn"
            onClick={() => setImportOpen(true)}
            variant="outline"
            className="rounded-full border-[#e5e5eb] hover:border-[#0a0a0a] whitespace-nowrap"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
        </div>
      </div>
      <div className="bg-white border border-[#e5e5eb] rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#5a5a65]">
            {customers.length === 0 ? "No customers yet. Add one manually or wait for them to check in at the kiosk." : "No matches."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f9f9fb] hover:bg-[#f9f9fb]">
                <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Customer</TableHead>
                <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Visits</TableHead>
                <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Points</TableHead>
                <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Lifetime</TableHead>
                <TableHead className="text-right uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.customer_id} className="hover:bg-[#f9f9fb]">
                  <TableCell>
                    <div className="font-semibold">{c.name || "—"}</div>
                    <div className="text-xs text-[#5a5a65] font-mono-data">{fmtPhone(c.phone)}</div>
                  </TableCell>
                  <TableCell className="font-mono-data">{c.visits}</TableCell>
                  <TableCell>
                    <button
                      data-testid={`customer-points-${c.customer_id}`}
                      onClick={() => setAdjustFor(c)}
                      className="font-mono-data font-bold text-[#002fa7] hover:bg-[#002fa7]/10 rounded-md px-2 py-1 -mx-2 transition"
                      title="Tap to adjust"
                    >
                      {c.points.toLocaleString()}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono-data text-[#5a5a65]">{c.lifetime_points.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button
                        size="sm"
                        data-testid={`customer-give-btn-${c.customer_id}`}
                        onClick={() => setGiveFor(c)}
                        className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white h-8 px-3 text-xs"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Give
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`customer-redeem-btn-${c.customer_id}`}
                        onClick={() => setRedeemFor(c)}
                        className="rounded-full border-[#e5e5eb] hover:border-[#0a0a0a] h-8 px-3 text-xs"
                      >
                        <Gift className="w-3.5 h-3.5 mr-1" /> Redeem
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`customer-menu-btn-${c.customer_id}`}
                            className="p-2 rounded-full text-[#5a5a65] hover:text-[#0a0a0a] hover:bg-[#f0f0f4] transition"
                            aria-label="More actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            data-testid={`customer-edit-${c.customer_id}`}
                            onClick={() => setEditFor(c)}
                          >
                            <Pencil className="w-4 h-4 mr-2" /> Edit info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`customer-delete-${c.customer_id}`}
                            onClick={() => removeCustomer(c)}
                            className="text-[#c00] focus:text-[#c00]"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <RedeemDialog
        open={!!redeemFor}
        onOpenChange={(o) => !o && setRedeemFor(null)}
        customer={redeemFor ? { id: redeemFor.customer_id, name: redeemFor.name, phone: redeemFor.phone } : null}
        customerPoints={redeemFor?.points || 0}
        onDone={async () => { setRedeemFor(null); await load(); await onAction?.(); }}
      />
      <GivePointsDialog
        open={!!giveFor}
        onOpenChange={(o) => !o && setGiveFor(null)}
        customer={giveFor}
        business={business}
        onDone={async () => { setGiveFor(null); await load(); await onAction?.(); }}
      />
      <AdjustPointsDialog
        open={!!adjustFor}
        onOpenChange={(o) => !o && setAdjustFor(null)}
        customer={adjustFor}
        onDone={async () => { setAdjustFor(null); await load(); await onAction?.(); }}
      />
      <EditCustomerDialog
        open={!!editFor}
        onOpenChange={(o) => !o && setEditFor(null)}
        customer={editFor}
        onDone={async () => { setEditFor(null); await load(); }}
      />
      <AddCustomerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={load}
      />
      <ImportCustomersDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onDone={load}
      />
    </div>
  );
}

// Give points: by purchase amount or by direct point count. Mirrors how the
// merchant gives points to a customer during a transaction.
function GivePointsDialog({ open, onOpenChange, customer, business, onDone }) {
  const [mode, setMode] = useState("amount");
  const [amount, setAmount] = useState("");
  const [points, setPoints] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setMode("amount"); setAmount(""); setPoints(""); }
  }, [open]);

  if (!customer) return null;
  const ppd = business?.points_per_dollar || 10;
  const previewPoints = mode === "amount"
    ? Math.round((Number(amount) || 0) * ppd)
    : Math.max(0, Math.round(Number(points) || 0));

  const submit = async () => {
    if (previewPoints <= 0) return toast.error("Enter a value greater than zero");
    setBusy(true);
    try {
      await api.post("/business/check-in", {
        phone: customer.phone,
        name: customer.name || "",
        amount: mode === "amount" ? Number(amount) : null,
        custom_points: mode === "points" ? previewPoints : null,
      });
      toast.success(`Added ${previewPoints} pts to ${customer.name || fmtPhone(customer.phone)}`);
      await onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="give-points-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Give points</DialogTitle>
          <DialogDescription>
            {customer.name || fmtPhone(customer.phone)} · current balance{" "}
            <span className="font-mono-data font-bold text-[#002fa7]">{customer.points}</span> pts
          </DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="amount" data-testid="give-mode-amount">By purchase $</TabsTrigger>
            <TabsTrigger value="points" data-testid="give-mode-points">Custom points</TabsTrigger>
          </TabsList>
          <TabsContent value="amount" className="mt-4 space-y-2">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65] font-mono-data">$</span>
              <Input
                data-testid="give-amount-input"
                type="number" step="0.01" min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                autoFocus
                className="h-12 pl-8 rounded-xl border-[#e5e5eb] font-mono-data"
              />
            </div>
            <p className="text-xs text-[#5a5a65]">
              {ppd} pts per $1 · earns <span className="font-mono-data font-bold text-[#002fa7]">{previewPoints}</span> pts
            </p>
          </TabsContent>
          <TabsContent value="points" className="mt-4 space-y-2">
            <Input
              data-testid="give-points-input"
              type="number" min="1" step="1"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder="Points to give"
              autoFocus
              className="h-12 rounded-xl border-[#e5e5eb] font-mono-data"
            />
            <p className="text-xs text-[#5a5a65]">Direct grant — no purchase needed.</p>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
          <Button
            data-testid="give-submit-btn"
            disabled={busy || previewPoints <= 0}
            onClick={submit}
            className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
          >
            {busy ? "Adding…" : `Give ${previewPoints} pts`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Adjust points directly: add to or subtract from balance with an optional note.
// Triggered by tapping the points cell in the customers table.
function AdjustPointsDialog({ open, onOpenChange, customer, onDone }) {
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState("add"); // 'add' | 'remove'

  useEffect(() => {
    if (open) { setDelta(""); setNote(""); setSign("add"); }
  }, [open]);

  if (!customer) return null;
  const magnitude = Math.max(0, Math.round(Number(delta) || 0));
  const signedDelta = sign === "add" ? magnitude : -magnitude;
  const newBalance = customer.points + signedDelta;
  const insufficient = sign === "remove" && newBalance < 0;

  const submit = async () => {
    if (magnitude <= 0) return toast.error("Enter an amount");
    if (insufficient) return toast.error("Customer doesn't have that many points");
    setBusy(true);
    try {
      await api.post(`/business/customers/${customer.customer_id}/adjust-points`, {
        delta: signedDelta,
        note: note.trim() || null,
      });
      toast.success(signedDelta > 0 ? `Added ${magnitude} pts` : `Removed ${magnitude} pts`);
      await onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="adjust-points-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Adjust points</DialogTitle>
          <DialogDescription>
            {customer.name || fmtPhone(customer.phone)} · current balance{" "}
            <span className="font-mono-data font-bold text-[#002fa7]">{customer.points}</span> pts
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="adjust-sign-add"
              onClick={() => setSign("add")}
              className={`flex items-center justify-center gap-2 h-12 rounded-xl border-2 font-semibold transition ${
                sign === "add"
                  ? "border-[#009e60] bg-[#009e60]/5 text-[#009e60]"
                  : "border-[#e5e5eb] text-[#5a5a65]"
              }`}
            >
              <Plus className="w-4 h-4" /> Add
            </button>
            <button
              type="button"
              data-testid="adjust-sign-remove"
              onClick={() => setSign("remove")}
              className={`flex items-center justify-center gap-2 h-12 rounded-xl border-2 font-semibold transition ${
                sign === "remove"
                  ? "border-[#c00] bg-[#c00]/5 text-[#c00]"
                  : "border-[#e5e5eb] text-[#5a5a65]"
              }`}
            >
              <Minus className="w-4 h-4" /> Remove
            </button>
          </div>
          <Input
            data-testid="adjust-amount-input"
            type="number" min="1" step="1"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="Points"
            autoFocus
            className="h-12 rounded-xl border-[#e5e5eb] font-mono-data text-lg"
          />
          <Input
            data-testid="adjust-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. birthday bonus"
            className="h-12 rounded-xl border-[#e5e5eb]"
          />
          {magnitude > 0 && (
            <div className={`text-sm font-mono-data ${insufficient ? "text-[#c00]" : "text-[#5a5a65]"}`}>
              New balance: <span className="font-bold">{Math.max(0, newBalance)}</span> pts
              {insufficient && " — not enough to deduct"}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
          <Button
            data-testid="adjust-submit-btn"
            disabled={busy || magnitude <= 0 || insufficient}
            onClick={submit}
            className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit customer info — name and/or phone.
function EditCustomerDialog({ open, onOpenChange, customer, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setName(customer.name || "");
      setPhone(customer.phone || "");
    }
  }, [open, customer]);

  if (!customer) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return toast.error("Phone is required");
    setBusy(true);
    try {
      await api.patch(`/business/customers/${customer.customer_id}`, {
        name: name.trim(),
        phone: phone.trim(),
      });
      toast.success("Customer updated");
      await onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-customer-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Edit customer</DialogTitle>
          <DialogDescription>Update their name or phone.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#5a5a65] mb-2 block">Name</label>
            <Input
              data-testid="edit-customer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Optional"
              className="h-12 rounded-xl border-[#e5e5eb]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#5a5a65] mb-2 block">Phone *</label>
            <Input
              data-testid="edit-customer-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="h-12 rounded-xl border-[#e5e5eb] font-mono-data"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
            <Button
              data-testid="edit-customer-submit"
              type="submit"
              disabled={busy || !phone.trim()}
              className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddCustomerDialog({ open, onOpenChange, onAdded }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [startingPoints, setStartingPoints] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setPhone(""); setName(""); setStartingPoints(""); };

  const submit = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/business/customers", {
        phone: phone.trim(),
        name: name.trim(),
        starting_points: Number(startingPoints) || 0,
      });
      const label = data.customer.name || fmtPhone(data.customer.phone);
      if (data.already_existed) {
        toast.success(
          data.points_added > 0
            ? `${label} already on list — added ${data.points_added} pts (now ${data.points})`
            : `${label} is already on your list`
        );
      } else {
        toast.success(
          data.points_added > 0
            ? `${label} added with ${data.points_added} pts`
            : `${label} added`
        );
      }
      reset();
      onOpenChange(false);
      onAdded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="add-customer-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
            Add a customer
          </DialogTitle>
          <DialogDescription>
            Register a customer manually — they don't have to check in at the kiosk first.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#5a5a65] mb-2 block">
              Phone number *
            </label>
            <Input
              data-testid="add-customer-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="555 123 4567"
              required
              autoFocus
              className="h-12 rounded-xl border-[#e5e5eb] font-mono-data"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#5a5a65] mb-2 block">
              Name (optional)
            </label>
            <Input
              data-testid="add-customer-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="h-12 rounded-xl border-[#e5e5eb]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#5a5a65] mb-2 block">
              Starting points (optional)
            </label>
            <Input
              data-testid="add-customer-points"
              type="number"
              min="0"
              step="1"
              value={startingPoints}
              onChange={(e) => setStartingPoints(e.target.value)}
              placeholder="0"
              className="h-12 rounded-xl border-[#e5e5eb] font-mono-data"
            />
            <p className="text-[11px] text-[#5a5a65] mt-1.5">
              Leave at 0 to start fresh, or grant some points (e.g. as a welcome bonus or migrating from another program).
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full border-[#e5e5eb]"
            >
              Cancel
            </Button>
            <Button
              data-testid="add-customer-submit"
              type="submit"
              disabled={busy || !phone.trim()}
              className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white"
            >
              {busy ? "Adding…" : "Add customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Bulk import customers from a CSV. The file is parsed client-side so the
// merchant can preview and fix issues before sending anything to the server.
function ImportCustomersDialog({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState("pick"); // 'pick' | 'preview' | 'done'
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("pick");
    setFileName("");
    setRows([]);
    setInvalid([]);
    setResult(null);
  };

  // Very small, dependency-free CSV parser. Handles quoted fields, embedded
  // commas, and CRLF line endings. Sufficient for typical customer exports.
  const parseCsv = (text) => {
    const out = [];
    let row = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { cur += c; }
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ",") { row.push(cur); cur = ""; }
        else if (c === "\n" || c === "\r") {
          if (cur !== "" || row.length) { row.push(cur); out.push(row); row = []; cur = ""; }
          if (c === "\r" && text[i + 1] === "\n") i++;
        }
        else cur += c;
      }
    }
    if (cur !== "" || row.length) { row.push(cur); out.push(row); }
    return out;
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    let text;
    try {
      text = await file.text();
    } catch {
      toast.error("Couldn't read that file");
      return;
    }
    const grid = parseCsv(text).filter((r) => r.some((c) => c && c.trim()));
    if (!grid.length) {
      toast.error("That CSV looks empty");
      return;
    }

    // Detect header row by checking if first row has known column names.
    const header = grid[0].map((h) => (h || "").trim().toLowerCase());
    const hasHeader = header.some((h) => ["phone", "name", "points", "starting_points", "starting points"].includes(h));
    const phoneIdx = hasHeader ? header.findIndex((h) => h === "phone" || h === "phone number" || h === "mobile") : 0;
    const nameIdx = hasHeader ? header.findIndex((h) => h === "name" || h === "first name" || h === "customer name") : 1;
    const pointsIdx = hasHeader
      ? header.findIndex((h) => h === "points" || h === "starting_points" || h === "starting points")
      : 2;
    const dataRows = hasHeader ? grid.slice(1) : grid;

    const valid = [];
    const bad = [];
    dataRows.forEach((r, i) => {
      const rawPhone = (phoneIdx >= 0 ? r[phoneIdx] : r[0]) || "";
      const phoneDigits = rawPhone.replace(/\D/g, "");
      if (phoneDigits.length < 7) {
        bad.push({ row: i + (hasHeader ? 2 : 1), phone: rawPhone, reason: "invalid phone" });
        return;
      }
      const name = (nameIdx >= 0 ? r[nameIdx] : r[1] || "") || "";
      const ptsRaw = (pointsIdx >= 0 ? r[pointsIdx] : r[2] || "") || "";
      const pts = Math.max(0, parseInt(String(ptsRaw).replace(/[^\d]/g, ""), 10) || 0);
      valid.push({ phone: phoneDigits, name: (name || "").trim(), starting_points: pts });
    });

    setRows(valid);
    setInvalid(bad);
    setStep("preview");
  };

  const submit = async () => {
    if (!rows.length) return toast.error("Nothing to import");
    setBusy(true);
    try {
      const { data } = await api.post("/business/customers/bulk-import", { rows });
      setResult(data);
      setStep("done");
      toast.success(`${data.created} new · ${data.updated} updated`);
      await onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="import-customers-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Import customers</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns: <span className="font-mono-data text-[#0a0a0a]">phone, name, points</span>{" "}
            (name and points optional).
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            <label
              className="flex flex-col items-center justify-center py-10 bg-[#f9f9fb] border-2 border-dashed border-[#e5e5eb] rounded-xl cursor-pointer hover:border-[#002fa7] transition-colors"
            >
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Upload className="w-8 h-8 text-[#5a5a65] mb-3" />
              <div className="font-semibold text-[#0a0a0a]">Tap to choose a CSV</div>
              <div className="text-xs text-[#5a5a65] mt-1">Up to 5,000 customers per file</div>
            </label>
            <div className="bg-[#002fa7]/5 border border-[#002fa7]/20 rounded-xl p-4 text-sm">
              <div className="font-semibold text-[#002fa7] mb-1">Example CSV</div>
              <pre className="text-xs font-mono-data text-[#5a5a65] whitespace-pre overflow-x-auto">{`phone,name,points
5551234567,Jane Doe,100
5559876543,Bob Smith,0
5552223344,,50`}</pre>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <FileText className="w-4 h-4 text-[#5a5a65]" />
              <span className="font-mono-data truncate flex-1">{fileName}</span>
              <button
                type="button"
                onClick={reset}
                className="text-xs uppercase tracking-wider font-bold text-[#002fa7] hover:underline"
              >
                Change file
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#009e60]/10 border border-[#009e60]/30 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#009e60]">Ready to import</div>
                <div className="font-display text-2xl font-extrabold text-[#009e60] font-mono-data">{rows.length}</div>
              </div>
              <div className={`rounded-xl p-3 border ${invalid.length ? "bg-[#c00]/10 border-[#c00]/30" : "bg-[#f0f0f4] border-[#e5e5eb]"}`}>
                <div className={`text-[10px] uppercase tracking-wider font-bold ${invalid.length ? "text-[#c00]" : "text-[#5a5a65]"}`}>Skipped</div>
                <div className={`font-display text-2xl font-extrabold font-mono-data ${invalid.length ? "text-[#c00]" : "text-[#5a5a65]"}`}>
                  {invalid.length}
                </div>
              </div>
            </div>
            {rows.length > 0 && (
              <div className="bg-white border border-[#e5e5eb] rounded-xl overflow-hidden">
                <div className="bg-[#f9f9fb] px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-[#5a5a65] flex">
                  <div className="flex-1">Phone</div>
                  <div className="flex-1">Name</div>
                  <div className="w-16 text-right">Points</div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {rows.slice(0, 50).map((r, i) => (
                    <div key={i} className="flex text-sm px-3 py-1.5 border-t border-[#f0f0f4]">
                      <div className="flex-1 font-mono-data truncate">{r.phone}</div>
                      <div className="flex-1 truncate">{r.name || <span className="text-[#5a5a65]">—</span>}</div>
                      <div className="w-16 text-right font-mono-data">{r.starting_points}</div>
                    </div>
                  ))}
                  {rows.length > 50 && (
                    <div className="px-3 py-2 text-xs text-[#5a5a65] text-center border-t border-[#f0f0f4]">
                      …and {rows.length - 50} more
                    </div>
                  )}
                </div>
              </div>
            )}
            {invalid.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-[#c00] font-semibold">
                  {invalid.length} row{invalid.length === 1 ? "" : "s"} will be skipped (bad phone)
                </summary>
                <ul className="mt-2 space-y-0.5 text-[#5a5a65]">
                  {invalid.slice(0, 10).map((b, i) => (
                    <li key={i} className="font-mono-data">Row {b.row}: "{b.phone}" — {b.reason}</li>
                  ))}
                  {invalid.length > 10 && <li>…and {invalid.length - 10} more</li>}
                </ul>
              </details>
            )}
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-4 py-2">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#009e60]/10 text-[#009e60] flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div className="font-display text-2xl font-extrabold">Import complete</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ImportStat label="Created" value={result.created} color="#009e60" />
              <ImportStat label="Updated" value={result.updated} color="#002fa7" />
              <ImportStat label="Points added" value={result.points_added.toLocaleString()} color="#0a0a0a" />
              <ImportStat label="Skipped" value={result.invalid_count || 0} color="#c00" />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "pick" && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">
              Cancel
            </Button>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-[#e5e5eb]">
                Cancel
              </Button>
              <Button
                data-testid="import-submit-btn"
                disabled={busy || rows.length === 0}
                onClick={submit}
                className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
              >
                {busy ? "Importing…" : `Import ${rows.length} customer${rows.length === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportStat({ label, value, color }) {
  return (
    <div className="bg-white border border-[#e5e5eb] rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#5a5a65]">{label}</div>
      <div className="font-display text-2xl font-extrabold font-mono-data" style={{ color }}>{value}</div>
    </div>
  );
}


// ---------------- Rewards ----------------
function MerchantRewards() {
  const [rewards, setRewards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", points_cost: 100 });
  const [submitting, setSubmitting] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/business/rewards");
      setRewards(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    setSubmitting(true);
    try {
      await api.post("/business/rewards", {
        title: form.title.trim(),
        description: form.description,
        points_cost: Number(form.points_cost) || 0,
      });
      setOpen(false);
      setForm({ title: "", description: "", points_cost: 100 });
      await load();
      toast.success("Reward created");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/business/rewards/${id}`);
      await load();
      toast.success("Removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">Rewards catalog</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">What they unlock.</h1>
        </div>
        <Button data-testid="rewards-add-btn" onClick={() => setOpen(true)}
          className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-6">
          <Plus className="w-4 h-4 mr-2" /> New reward
        </Button>
      </div>

      {rewards.length === 0 ? (
        <div className="bg-white border border-[#e5e5eb] rounded-2xl p-16 text-center">
          <div className="w-12 h-12 rounded-full bg-[#f0f0f4] flex items-center justify-center mx-auto mb-4">
            <Gift className="w-6 h-6 text-[#002fa7]" />
          </div>
          <h3 className="font-display text-2xl font-extrabold mb-2">No rewards yet</h3>
          <p className="text-[#5a5a65] mb-6">Optional — you can redeem custom amounts without saving rewards.</p>
          <Button onClick={() => setOpen(true)} className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">Create reward</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((r) => (
            <div key={r.id} className="bg-white border border-[#e5e5eb] rounded-2xl p-6 flex flex-col hover:border-[#0a0a0a] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#002fa7]/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#002fa7]" />
                </div>
                <button onClick={() => remove(r.id)} className="text-[#5a5a65] hover:text-[#c00] p-1.5 rounded-lg hover:bg-[#f9f9fb]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="font-display text-xl font-bold mb-1">{r.title}</div>
              {r.description && <div className="text-sm text-[#5a5a65] mb-4 line-clamp-2">{r.description}</div>}
              <div className="mt-auto">
                <div className="inline-flex items-baseline gap-1 bg-[#f9f9fb] rounded-full px-4 py-1.5">
                  <span className="font-mono-data font-bold text-lg text-[#002fa7]">{r.points_cost}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-[#5a5a65] font-semibold">pts</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">New reward</DialogTitle>
            <DialogDescription>Customers redeem these with their points.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="reward-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Free small coffee" className="h-12 rounded-xl border-[#e5e5eb]" autoFocus required />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Any size under 8oz" className="h-12 rounded-xl border-[#e5e5eb]" />
            </div>
            <div>
              <Label>Points cost</Label>
              <Input type="number" min="1" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: e.target.value })} className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
              <Button data-testid="reward-submit-btn" type="submit" disabled={submitting} className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">{submitting ? "Creating…" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MerchantPromotions({ business }) {
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // ad being edited, or null for new
  const [form, setForm] = useState({ headline: "", body: "", image_data: null, image_thumb: null, is_active: true, duration_seconds: 6, starts_on: "", ends_on: "" });
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiImgBusy, setAiImgBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/business/kiosk-ads");
      setAds(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ headline: "", body: "", image_data: null, image_thumb: null, is_active: true, duration_seconds: 6, starts_on: "", ends_on: "" });
    setAiPrompt("");
    setOpen(true);
  };

  const openEdit = async (ad) => {
    // Card-level `ad` excludes image_data (kept small for speed). Fetch the full
    // ad before opening the dialog so the user sees the existing image preview.
    try {
      const { data } = await api.get(`/business/kiosk-ads/${ad.id}`);
      setEditing(data);
      setForm({
        headline: data.headline || "",
        body: data.body || "",
        image_data: data.image_data || null,
        image_thumb: data.image_thumb || null,
        is_active: !!data.is_active,
        duration_seconds: data.duration_seconds || 6,
        starts_on: data.starts_on || "",
        ends_on: data.ends_on || "",
      });
      setOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Compress image client-side. Tablet cameras shoot 12MP+ photos (3-8MB raw),
  // which after base64 encoding can choke low-power tablet browsers when many
  // cards render at once. We produce TWO outputs:
  //   - full: 800px wide @ 0.7 JPEG (~80-150KB) — shown on the kiosk
  //   - thumb: 240px wide @ 0.6 JPEG (~10-25KB) — shown in merchant list cards
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });
      const resize = (maxW, quality) => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", quality);
      };
      const full = resize(800, 0.7);
      const thumb = resize(240, 0.6);
      setForm((f) => ({ ...f, image_data: full, image_thumb: thumb }));
    } catch {
      toast.error("Couldn't read that image");
    } finally {
      setCompressing(false);
    }
  };

  const aiDraft = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) return toast.error("Tell me about your promotion first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/business/promotions/ai-draft", { prompt });
      setForm((f) => ({
        ...f,
        headline: data.headline || f.headline,
        body: data.body || f.body,
        starts_on: data.starts_on || "",
        ends_on: data.ends_on || "",
      }));
      toast.success("Draft ready — review and edit before saving");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiBusy(false);
    }
  };

  // Generate a kiosk image with Gemini Nano Banana, then run it through the
  // same resize pipeline `handleFile` uses so we get matching full + thumb.
  const aiImage = async () => {
    // Prefer the merchant's AI prompt; fall back to the current headline+body so
    // the generated visual matches the copy if they already drafted it.
    const prompt = (aiPrompt.trim()
      || `${form.headline} ${form.body}`.trim());
    if (!prompt) return toast.error("Add a headline or describe the image first");
    setAiImgBusy(true);
    try {
      const { data } = await api.post("/business/promotions/ai-image", { prompt });
      const dataUrl = data?.image_data;
      if (!dataUrl) throw new Error("No image returned");
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });
      const resize = (maxW, quality) => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        return canvas.toDataURL("image/jpeg", quality);
      };
      setForm((f) => ({
        ...f,
        image_data: resize(800, 0.7),
        image_thumb: resize(240, 0.6),
      }));
      toast.success("Image generated — feel free to swap it out");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiImgBusy(false);
    }
  };


  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_data && !form.headline.trim()) {
      return toast.error("Add an image or a headline");
    }
    if (form.starts_on && form.ends_on && form.starts_on > form.ends_on) {
      return toast.error("End date must be on or after the start date");
    }
    const dur = Math.max(2, Math.min(120, Number(form.duration_seconds) || 6));
    setSubmitting(true);
    try {
      if (editing) {
        // Only send image_data if it changed (new upload) — otherwise null keeps existing.
        const imageChanged = form.image_data !== editing.image_data;
        await api.put(`/business/kiosk-ads/${editing.id}`, {
          headline: form.headline,
          body: form.body,
          image_data: imageChanged ? form.image_data : null,
          image_thumb: imageChanged ? form.image_thumb : null,
          is_active: form.is_active,
          duration_seconds: dur,
          starts_on: form.starts_on || null,
          ends_on: form.ends_on || null,
        });
        toast.success("Promotion updated");
      } else {
        await api.post("/business/kiosk-ads", {
          headline: form.headline,
          body: form.body,
          image_data: form.image_data,
          image_thumb: form.image_thumb,
          is_active: form.is_active,
          duration_seconds: dur,
          starts_on: form.starts_on || null,
          ends_on: form.ends_on || null,
        });
        toast.success("Promotion added");
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const toggleActive = async (ad) => {
    try {
      await api.put(`/business/kiosk-ads/${ad.id}`, {
        headline: ad.headline,
        body: ad.body,
        is_active: !ad.is_active,
        image_data: null, // keep existing image
        image_thumb: null, // keep existing thumb
        duration_seconds: ad.duration_seconds || 6,
        starts_on: ad.starts_on || null,
        ends_on: ad.ends_on || null,
      });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promotion?")) return;
    try {
      await api.delete(`/business/kiosk-ads/${id}`);
      await load();
      toast.success("Removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const kioskUrl = `${window.location.origin}/kiosk/${business?.slug}`;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">Kiosk promotions</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">What they see.</h1>
          <p className="text-[#5a5a65] mt-3 max-w-xl">
            These show on your customer-facing kiosk during check-in and as a full-screen
            slideshow when the kiosk is idle. Toggle active ads on/off anytime.
          </p>
        </div>
        <Button data-testid="promo-add-btn" onClick={openCreate}
          className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-6">
          <Plus className="w-4 h-4 mr-2" /> New promotion
        </Button>
      </div>

      {ads.length === 0 ? (
        <div className="bg-white border border-[#e5e5eb] rounded-2xl p-16 text-center">
          <div className="w-12 h-12 rounded-full bg-[#f0f0f4] flex items-center justify-center mx-auto mb-4">
            <Megaphone className="w-6 h-6 text-[#002fa7]" />
          </div>
          <h3 className="font-display text-2xl font-extrabold mb-2">No promotions yet</h3>
          <p className="text-[#5a5a65] mb-6">Add an image and a headline to advertise on your kiosk.</p>
          <Button data-testid="promo-empty-add-btn" onClick={openCreate} className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">Create promotion</Button>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e5eb] rounded-2xl overflow-hidden divide-y divide-[#e5e5eb]">
          {ads.map((ad) => (
            <div
              key={ad.id}
              data-testid={`promo-card-${ad.id}`}
              className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 transition-colors ${
                ad.is_active ? "" : "opacity-60"
              }`}
              style={{ contentVisibility: "auto", containIntrinsicSize: "0 80px" }}
            >
              {/* Small thumbnail — lazy/async decoded so the page never stalls.
                  Off-screen rows are skipped via content-visibility:auto. */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#f0f0f4] flex items-center justify-center overflow-hidden flex-shrink-0">
                {ad.image_thumb ? (
                  <img
                    src={ad.image_thumb}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-[#5a5a65]" />
                )}
              </div>

              {/* Headline + body */}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-base leading-tight truncate">
                  {ad.headline || <span className="text-[#5a5a65] italic">No headline</span>}
                </div>
                {ad.body && (
                  <div className="text-xs text-[#5a5a65] mt-0.5 truncate">{ad.body}</div>
                )}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <button
                    data-testid={`promo-toggle-${ad.id}`}
                    onClick={() => toggleActive(ad)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      ad.is_active
                        ? "bg-[#009e60]/10 text-[#009e60]"
                        : "bg-[#f0f0f4] text-[#5a5a65]"
                    }`}
                  >
                    {ad.is_active ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                    {ad.is_active ? "Live" : "Hidden"}
                  </button>
                  {ad.is_active && ad.schedule_status === "scheduled" && (
                    <span
                      data-testid={`promo-schedule-${ad.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#f5a623]/15 text-[#a06200]"
                      title={ad.starts_on ? `Starts ${ad.starts_on}` : ""}
                    >
                      Scheduled
                    </span>
                  )}
                  {ad.is_active && ad.schedule_status === "ended" && (
                    <span
                      data-testid={`promo-schedule-${ad.id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#f0f0f4] text-[#5a5a65]"
                      title={ad.ends_on ? `Ended ${ad.ends_on}` : ""}
                    >
                      Ended
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5a5a65] font-mono-data">
                    {ad.duration_seconds || 6}s
                  </span>
                  {(ad.starts_on || ad.ends_on) && (
                    <span className="text-[10px] text-[#5a5a65] tracking-wider">
                      {ad.starts_on || "anytime"} → {ad.ends_on || "no end"}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-1 flex-shrink-0">
                <button
                  data-testid={`promo-edit-${ad.id}`}
                  onClick={() => openEdit(ad)}
                  className="p-2.5 rounded-lg text-[#5a5a65] hover:text-[#0a0a0a] hover:bg-[#f9f9fb] active:scale-95 transition"
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`promo-delete-${ad.id}`}
                  onClick={() => remove(ad.id)}
                  className="p-2.5 rounded-lg text-[#5a5a65] hover:text-[#c00] hover:bg-[#f9f9fb] active:scale-95 transition"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {ads.length > 0 && (
        <div className="mt-8 bg-[#002fa7]/5 border border-[#002fa7]/20 rounded-2xl p-5 flex items-start gap-3">
          <Tablet className="w-5 h-5 text-[#002fa7] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#002fa7]">Preview your kiosk</div>
            <a
              href={kioskUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#002fa7] underline break-all"
            >
              {kioskUrl}
            </a>
            <div className="text-[#5a5a65] mt-1">Each active promotion shows for the seconds you set, then rotates to the next.</div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">
              {editing ? "Edit promotion" : "New promotion"}
            </DialogTitle>
            <DialogDescription>
              An image, a headline, or both. Customers see this on your kiosk.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="promo-form">
            <div className="rounded-2xl bg-[#002fa7]/5 border border-[#002fa7]/20 p-3 sm:p-4 space-y-2">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] font-bold text-[#002fa7]">
                <Sparkles className="w-3.5 h-3.5" /> AI draft
              </div>
              <div className="text-xs text-[#5a5a65] leading-relaxed">
                Describe your promo in plain English — Claude writes the headline, subtext,
                and resolves dates like "this weekend" or "Black Friday".
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="promo-ai-prompt"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder='e.g. "Black Friday weekend, 20% off espresso"'
                  maxLength={600}
                  className="h-11 rounded-xl border-[#e5e5eb] bg-white flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      aiDraft();
                    }
                  }}
                />
                <Button
                  type="button"
                  data-testid="promo-ai-draft-btn"
                  onClick={aiDraft}
                  disabled={aiBusy || aiImgBusy || !aiPrompt.trim()}
                  className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-xl px-4 h-11 flex-shrink-0"
                >
                  {aiBusy ? "Thinking…" : "Draft"}
                </Button>
              </div>
              <Button
                type="button"
                data-testid="promo-ai-image-btn"
                onClick={aiImage}
                disabled={aiImgBusy || aiBusy || (!aiPrompt.trim() && !form.headline.trim())}
                variant="outline"
                className="w-full h-11 rounded-xl border-[#002fa7]/30 text-[#002fa7] hover:bg-[#002fa7]/10 font-semibold"
              >
                <ImageIcon className="w-4 h-4 mr-1.5" />
                {aiImgBusy ? "Generating image (10–20s)…" : "Generate image"}
              </Button>
            </div>
            <div>
              <Label>Image (optional)</Label>
              <div className="mt-1.5">
                {form.image_data ? (
                  <div
                    className="relative aspect-video bg-[#f0f0f4] rounded-xl overflow-hidden"
                    style={{
                      backgroundImage: `url("${form.image_data}")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, image_data: null }))}
                      className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 hover:bg-white"
                      data-testid="promo-image-clear"
                    >
                      <Trash2 className="w-4 h-4 text-[#c00]" />
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex flex-col items-center justify-center aspect-video bg-[#f9f9fb] border-2 border-dashed border-[#e5e5eb] rounded-xl cursor-pointer hover:border-[#002fa7] transition-colors"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="promo-image-input"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    {compressing ? (
                      <div className="text-sm text-[#5a5a65] font-semibold">Compressing…</div>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-[#5a5a65] mb-2" />
                        <div className="text-sm text-[#0a0a0a] font-semibold">Tap to upload</div>
                        <div className="text-xs text-[#5a5a65] mt-0.5">JPG, PNG — auto-resized</div>
                      </>
                    )}
                  </label>
                )}
              </div>
            </div>
            <div>
              <Label>Headline (optional)</Label>
              <Input
                data-testid="promo-headline-input"
                value={form.headline}
                onChange={(e) => setForm({ ...form, headline: e.target.value })}
                placeholder="Happy hour 4–6pm"
                maxLength={80}
                className="h-12 rounded-xl border-[#e5e5eb]"
              />
            </div>
            <div>
              <Label>Subtext (optional)</Label>
              <Input
                data-testid="promo-body-input"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="$2 off any drink"
                maxLength={120}
                className="h-12 rounded-xl border-[#e5e5eb]"
              />
            </div>
            <div>
              <Label>Seconds on screen before rotating</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <Input
                  data-testid="promo-duration-input"
                  type="number"
                  min={2}
                  max={120}
                  value={form.duration_seconds}
                  onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
                  className="h-12 w-28 rounded-xl border-[#e5e5eb] font-mono-data text-center"
                  required
                />
                <span className="text-sm text-[#5a5a65]">seconds (2–120)</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="promo-starts-on">Start date (optional)</Label>
                <Input
                  id="promo-starts-on"
                  data-testid="promo-starts-on-input"
                  type="date"
                  value={form.starts_on}
                  onChange={(e) => setForm({ ...form, starts_on: e.target.value })}
                  className="h-12 rounded-xl border-[#e5e5eb] font-mono-data mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="promo-ends-on">End date (optional)</Label>
                <Input
                  id="promo-ends-on"
                  data-testid="promo-ends-on-input"
                  type="date"
                  value={form.ends_on}
                  onChange={(e) => setForm({ ...form, ends_on: e.target.value })}
                  className="h-12 rounded-xl border-[#e5e5eb] font-mono-data mt-1.5"
                />
              </div>
              <p className="col-span-2 text-xs text-[#5a5a65] -mt-1">
                Leave both blank to run forever. Set only an end date for a "until X" promo,
                or only a start date to schedule a future launch.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="promo-active-checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 accent-[#002fa7]"
              />
              <span className="text-sm font-semibold">Show on kiosk now</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#e5e5eb]">Cancel</Button>
              <Button
                data-testid="promo-submit-btn"
                type="submit"
                disabled={submitting || compressing}
                className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
              >
                {submitting ? "Saving…" : editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Analytics ----------------
function MerchantAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/business/analytics?days=${days}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const maxVisits = data ? Math.max(1, ...data.daily_visits.map((d) => d.visits)) : 1;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">Analytics</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">
            How you're doing.
          </h1>
        </div>
        <div className="inline-flex p-1 bg-[#f0f0f4] rounded-full" role="tablist">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                days === d ? "bg-white text-[#0a0a0a] shadow-sm" : "text-[#5a5a65]"
              }`}
            >
              Last {d}d
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-sm text-[#5a5a65] py-16 text-center">Crunching numbers…</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <KpiCard
              label="Revenue tracked"
              value={`$${data.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={DollarSign}
              testId="kpi-revenue"
            />
            <KpiCard
              label="Visits"
              value={data.visits.toLocaleString()}
              icon={CheckCircle2}
              testId="kpi-visits"
            />
            <KpiCard
              label="Active customers"
              value={data.active_customers.toLocaleString()}
              icon={Users}
              testId="kpi-active"
            />
            <KpiCard
              label="New customers"
              value={data.new_customers.toLocaleString()}
              icon={TrendingUp}
              testId="kpi-new"
            />
          </div>

          {/* Points awarded vs redeemed */}
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-8">
            <div className="bg-white border border-[#e5e5eb] rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65] mb-2">
                Points awarded
              </div>
              <div className="font-display text-3xl font-extrabold text-[#009e60] font-mono-data">
                +{data.points_awarded.toLocaleString()}
              </div>
            </div>
            <div className="bg-white border border-[#e5e5eb] rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65] mb-2">
                Points redeemed
              </div>
              <div className="font-display text-3xl font-extrabold text-[#c00] font-mono-data">
                −{data.points_redeemed.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Daily visits chart — pure CSS, no library. */}
          <div className="bg-white border border-[#e5e5eb] rounded-2xl p-5 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65]">
                Visits per day
              </div>
              <div className="text-xs text-[#5a5a65]">Peak: {maxVisits}</div>
            </div>
            {data.visits === 0 ? (
              <div className="py-10 text-center text-sm text-[#5a5a65]">
                No visits in this range yet.
              </div>
            ) : (
              <div data-testid="analytics-chart" className="flex items-end gap-[2px] sm:gap-1 h-36">
                {data.daily_visits.map((d) => {
                  const h = Math.max(2, Math.round((d.visits / maxVisits) * 100));
                  return (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.visits} visit${d.visits === 1 ? "" : "s"}`}
                      className="flex-1 rounded-t bg-[#002fa7]/15 hover:bg-[#002fa7] transition-colors min-w-0"
                      style={{ height: `${h}%` }}
                    />
                  );
                })}
              </div>
            )}
            <div className="flex justify-between text-[10px] text-[#5a5a65] mt-2 font-mono-data">
              <span>{data.daily_visits[0]?.date.slice(5)}</span>
              <span>{data.daily_visits[data.daily_visits.length - 1]?.date.slice(5)}</span>
            </div>
          </div>

          {/* Top customers */}
          <div className="bg-white border border-[#e5e5eb] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e5e5eb]">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65]">
                Top customers (lifetime)
              </div>
            </div>
            {data.top_customers.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#5a5a65]">No customers yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f9f9fb] hover:bg-[#f9f9fb]">
                    <TableHead className="uppercase tracking-[0.16em] text-[10px] font-semibold text-[#5a5a65]">#</TableHead>
                    <TableHead className="uppercase tracking-[0.16em] text-[10px] font-semibold text-[#5a5a65]">Customer</TableHead>
                    <TableHead className="uppercase tracking-[0.16em] text-[10px] font-semibold text-[#5a5a65]">Visits</TableHead>
                    <TableHead className="uppercase tracking-[0.16em] text-[10px] font-semibold text-[#5a5a65]">Balance</TableHead>
                    <TableHead className="text-right uppercase tracking-[0.16em] text-[10px] font-semibold text-[#5a5a65]">Lifetime</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_customers.map((c, i) => (
                    <TableRow key={c.customer_id} data-testid={`top-customer-${i}`}>
                      <TableCell className="font-mono-data text-[#5a5a65]">{i + 1}</TableCell>
                      <TableCell>
                        <div className="font-semibold">{c.name || "—"}</div>
                        <div className="text-xs text-[#5a5a65] font-mono-data">{fmtPhone(c.phone)}</div>
                      </TableCell>
                      <TableCell className="font-mono-data">{c.visits}</TableCell>
                      <TableCell className="font-mono-data font-bold text-[#002fa7]">{c.points.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono-data font-bold">{c.lifetime_points.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#e5e5eb] rounded-2xl p-4 sm:p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#5a5a65]">{label}</div>
        <Icon className="w-4 h-4 text-[#002fa7]" />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight font-mono-data truncate">{value}</div>
    </div>
  );
}

function MerchantMarketing() {
  const [cfg, setCfg] = useState(null);
  const [audience, setAudience] = useState(null);
  const [blasts, setBlasts] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [c, a, b] = await Promise.all([
        api.get("/business/sms-config"),
        api.get("/business/sms-audience"),
        api.get("/business/sms-blasts"),
      ]);
      setCfg(c.data);
      setAudience(a.data);
      setBlasts(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    const m = message.trim();
    if (!m) { toast.error("Type a message first"); return; }
    if (!window.confirm(`Send this message to ${audience?.opted_in || 0} customer(s)?\n\n"${m}"`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/business/sms-blast", { message: m });
      toast.success(`Sent ${data.sent} message(s)${data.failed ? ` · ${data.failed} failed` : ""}`);
      setMessage("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  // Always-appended legal opt-out hint preview
  const previewBody = message.trim().toUpperCase().includes("STOP")
    ? message.trim()
    : (message.trim() ? message.trim() + "\nReply STOP to opt out." : "");
  const charCount = previewBody.length;

  if (loading) return <div className="p-10 text-sm uppercase tracking-[0.2em] text-[#5a5a65]">Loading…</div>;

  if (!cfg?.configured) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="sms-marketing-needs-config">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">SMS Marketing</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95] mb-6">Connect Twilio first.</h1>
        <div className="bg-white border border-[#e5e5eb] rounded-2xl p-8">
          <p className="text-[#5a5a65] mb-5 leading-relaxed">
            SparkLoop sends SMS using your own Twilio account, so you control the cost (~$0.008 per text) and the sending number. Connect it once in Settings, then come back here to send blasts.
          </p>
          <Button onClick={() => navigate("/merchant/settings")} className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white" data-testid="sms-go-settings">
            Go to Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="sms-marketing-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">SMS Marketing</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">Talk to your regulars.</h1>
        <p className="text-[#5a5a65] mt-2">Send a one-off blast to customers who opted in at the kiosk. STOP / unsubscribes are handled automatically.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total customers" value={audience?.total_customers ?? 0} testid="sms-stat-total" />
        <StatCard label="Opted in" value={audience?.opted_in ?? 0} testid="sms-stat-opted-in" highlight />
        <StatCard label="Blasts sent" value={blasts.length} testid="sms-stat-blasts" />
        <StatCard label="Sending number" value={cfg.from_number || "—"} mono small testid="sms-stat-from" />
      </div>

      <div className="bg-white border border-[#e5e5eb] rounded-2xl p-6 sm:p-8 mb-8">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">New blast</div>
        <h2 className="font-display text-2xl font-extrabold mb-4">Compose</h2>
        <textarea
          data-testid="sms-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          maxLength={280}
          placeholder="e.g. Happy Friday! 20% off all drinks today only. Show this text at the counter."
          className="w-full p-4 rounded-2xl border-2 border-[#e5e5eb] focus:border-[#002fa7] outline-none text-sm leading-relaxed resize-none"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div className="text-xs text-[#5a5a65] font-mono-data">
            {charCount} chars · {charCount > 160 ? "2 SMS segments" : "1 SMS segment"} · ~${((audience?.opted_in || 0) * (charCount > 160 ? 0.016 : 0.008)).toFixed(2)} est.
          </div>
          <Button
            data-testid="sms-send-btn"
            onClick={send}
            disabled={sending || !message.trim() || (audience?.opted_in || 0) === 0}
            className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white"
          >
            <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : `Send to ${audience?.opted_in || 0}`}
          </Button>
        </div>
        {previewBody && (
          <div className="mt-5 pt-5 border-t border-[#e5e5eb]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-2">Preview</div>
            <div className="bg-[#f9f9fb] rounded-2xl p-4 text-sm whitespace-pre-wrap leading-relaxed max-w-md">{previewBody}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#e5e5eb] rounded-2xl p-6 sm:p-8">
        <h2 className="font-display text-2xl font-extrabold mb-4">Recent blasts</h2>
        {blasts.length === 0 ? (
          <p className="text-sm text-[#5a5a65]">No blasts sent yet.</p>
        ) : (
          <div className="space-y-3">
            {blasts.map((b) => (
              <div key={b.id} className="border border-[#e5e5eb] rounded-2xl p-4" data-testid={`sms-blast-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="text-xs font-mono-data text-[#5a5a65]">{new Date(b.created_at).toLocaleString()}</div>
                  <div className="text-xs font-bold">
                    <span className="text-[#009e60]">{b.sent_count} sent</span>
                    {b.failed_count > 0 && <span className="text-[#ff3355] ml-2">· {b.failed_count} failed</span>}
                  </div>
                </div>
                <div className="text-sm whitespace-pre-wrap text-[#0a0a0a]">{b.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, mono, small, highlight, testid }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-[#002fa7] text-white border-[#002fa7]" : "bg-white border-[#e5e5eb]"}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${highlight ? "opacity-80" : "text-[#5a5a65]"} mb-1`}>{label}</div>
      <div className={`${mono ? "font-mono-data" : "font-display font-extrabold"} ${small ? "text-sm break-all" : "text-3xl"} leading-tight`}>{value}</div>
    </div>
  );
}


function MerchantSettings({ business, user, billing, onRefreshBilling }) {
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">Settings</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">Your setup.</h1>
      </div>

      {billing && (
        <BillingCard billing={billing} onRefresh={onRefreshBilling} />
      )}

      <div className="bg-white border border-[#e5e5eb] rounded-2xl p-8 space-y-5">
        <Row label="Business name" value={business.name} />
        <Row label="Category" value={business.category} />
        <EditablePointsRow business={business} />
        <Row label="Kiosk URL" value={`${window.location.origin}/kiosk/${business.slug}`} mono />
        <Row label="Signed in as" value={user?.name ? `${user.name} · @${user.username}` : `@${user?.username}`} />
        <div className="pt-4 border-t border-[#e5e5eb]">
          <Button
            data-testid="merchant-change-pw-btn"
            variant="outline"
            onClick={() => setPwOpen(true)}
            className="rounded-full border-[#e5e5eb] hover:border-[#0a0a0a]"
          >
            Change password
          </Button>
          <p className="text-xs text-[#5a5a65] mt-3">
            For business profile changes (name, branding, billing) contact your SparkLoop admin.
          </p>
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <SmsConfigCard business={business} />
      <KioskLockCard />
    </div>
  );
}

function KioskLockCard() {
  const { locked, lock } = useKioskLock();
  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-8 mt-6" data-testid="kiosk-lock-card">
      <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-1">Tablet · Kiosk lock</div>
      <div className="font-display text-2xl font-extrabold mb-3 leading-tight">Lock this tablet as a kiosk</div>
      <p className="text-sm text-[#5a5a65] leading-relaxed mb-5 max-w-2xl">
        Turn this on once the tablet is set up at the merchant's store. The "Sign out" button disappears — clerks can use every feature, but can't sign out, even after a refresh or tablet restart.
        <br /><br />
        To unlock later: <span className="font-semibold">long-press the SparkLoop logo (top-left) for 5 seconds</span>, then enter the SparkLoop admin password. Only you (the admin) can unlock — not the merchant or clerks.
      </p>
      {locked ? (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#dcf6e2] text-[#1a6b34] text-sm font-semibold">
          <CheckCircle2 className="w-4 h-4" /> This device is locked
        </div>
      ) : (
        <Button
          data-testid="kiosk-lock-btn"
          onClick={() => {
            if (!window.confirm("Lock this tablet as a kiosk?\n\nThe Sign-out button will disappear. To unlock, long-press the SparkLoop logo for 5 seconds and enter the SparkLoop ADMIN password.")) return;
            lock();
            toast.success("Kiosk locked on this device");
          }}
          className="rounded-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white"
        >
          🔒 Lock this device as kiosk
        </Button>
      )}
    </div>
  );
}

function SmsConfigCard({ business }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/business/sms-config");
      setCfg(data);
      setForm({
        account_sid: data.account_sid || "",
        auth_token: "",
        from_number: data.from_number || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.account_sid.trim() || !form.auth_token.trim() || !form.from_number.trim()) {
      toast.error("All three fields are required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/business/sms-config", form);
      toast.success("SMS credentials verified and saved");
      setEditing(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Disconnect Twilio? You won't be able to send SMS blasts until you reconnect.")) return;
    try {
      await api.delete("/business/sms-config");
      toast.success("Disconnected");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendTest = async () => {
    if (!testPhone.replace(/\D/g, "")) return;
    setTesting(true);
    try {
      await api.post("/business/sms-config/test", { to: testPhone });
      toast.success("Test SMS sent — check your phone");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  if (loading) return null;
  const webhookFullUrl = `${window.location.origin}${cfg?.webhook_url || ""}`;

  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-8 mt-6" data-testid="sms-config-card">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-1">SMS marketing</div>
          <div className="font-display text-2xl font-extrabold leading-tight">Twilio connection</div>
          <div className="text-xs text-[#5a5a65] mt-1">
            Connect your own Twilio account to send promotional texts to opted-in customers.
          </div>
        </div>
        {cfg?.configured && (
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full bg-[#dcf6e2] text-[#1a6b34] whitespace-nowrap">
            <CheckCircle2 className="w-3 h-3" /> Connected
          </div>
        )}
      </div>

      {!editing && cfg?.configured ? (
        <div className="space-y-4">
          <Row label="Account SID" value={cfg.account_sid} mono />
          <Row label="Auth token" value={cfg.auth_token_masked} mono />
          <Row label="Sending number" value={cfg.from_number} mono />
          <div className="pt-3 border-t border-[#e5e5eb]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">
              Compliance webhook (paste in Twilio number settings)
            </div>
            <div className="font-mono-data text-xs break-all bg-[#f9f9fb] px-3 py-2 rounded-lg">{webhookFullUrl}</div>
            <p className="text-[11px] text-[#5a5a65] mt-1.5 leading-snug">
              In Twilio Console → Phone Numbers → your sending number → set "A MESSAGE COMES IN" webhook to this URL (HTTP POST). Required for STOP keyword handling.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 items-end pt-3 border-t border-[#e5e5eb]">
            <div className="flex-1 min-w-[180px]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">Send a test SMS</div>
              <input
                data-testid="sms-test-phone"
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="w-full h-10 px-3 rounded-xl border border-[#e5e5eb] text-sm"
              />
            </div>
            <Button
              data-testid="sms-test-send"
              onClick={sendTest}
              disabled={testing || !testPhone.trim()}
              className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white"
            >
              {testing ? "Sending…" : "Send test"}
            </Button>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full" data-testid="sms-edit-btn">
              Update credentials
            </Button>
            <Button variant="outline" onClick={remove} className="rounded-full border-[#ff3355] text-[#ff3355] hover:bg-[#ff3355] hover:text-white" data-testid="sms-disconnect-btn">
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-[#fff4d6] border border-[#f0d890] rounded-xl p-3 text-xs text-[#7a5a00] leading-snug">
            Don't have Twilio yet? Sign up at <a href="https://twilio.com/try-twilio" target="_blank" rel="noreferrer" className="underline font-semibold">twilio.com/try-twilio</a> — costs about $1/mo for a phone number plus ~$0.008 per SMS, paid directly to Twilio. Once your account is live, copy the values below from your Twilio Console.
          </div>
          <FormRow label="Account SID" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={form.account_sid} onChange={(v) => setForm({ ...form, account_sid: v })} testid="sms-sid-input" />
          <FormRow label="Auth Token" placeholder="(your Twilio auth token)" value={form.auth_token} onChange={(v) => setForm({ ...form, auth_token: v })} testid="sms-token-input" type="password" />
          <FormRow label="Sending phone number" placeholder="+15551234567 (must be a number you own in Twilio)" value={form.from_number} onChange={(v) => setForm({ ...form, from_number: v })} testid="sms-from-input" />
          <div className="flex gap-2 pt-2">
            <Button data-testid="sms-save-btn" onClick={save} disabled={saving} className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white">
              {saving ? "Verifying…" : "Verify & save"}
            </Button>
            {cfg?.configured && (
              <Button variant="outline" onClick={() => { setEditing(false); load(); }} className="rounded-full">Cancel</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormRow({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 px-3 rounded-xl border border-[#e5e5eb] text-sm font-mono-data"
      />
    </div>
  );
}

function BillingBanner({ billing }) {
  const isPastDue = billing.status === "past_due";
  const inGrace = !!billing.in_grace;
  const isTrialing = billing.status === "trialing";
  // Severity: past_due > grace > trial-ending > active-ending
  const tone = isPastDue
    ? "red"
    : inGrace
    ? "red"
    : isTrialing
    ? "yellow"
    : "yellow"; // active but renewing soon
  const Icon = (isPastDue || inGrace) ? AlertTriangle : CreditCard;
  const goSettings = () => { window.location.href = "/merchant/settings"; };
  const price = (billing.monthly_price_usd || 0).toFixed(2);

  let message;
  if (isPastDue) {
    message = `Your subscription is past due — kiosk and SMS are paused. Pay $${price} to restore service.`;
  } else if (inGrace) {
    const d = billing.grace_days_left ?? 0;
    message = `Subscription expired. Grace period: ${d} day${d === 1 ? "" : "s"} left before your kiosk is paused. Renew now to avoid interruption.`;
  } else if (isTrialing) {
    message = `Trial ends in ${billing.days_left} day${billing.days_left === 1 ? "" : "s"}. Add a payment method to stay active.`;
  } else {
    message = `Your subscription renews in ${billing.days_left} day${billing.days_left === 1 ? "" : "s"}. Make sure your payment method is up to date.`;
  }

  return (
    <div
      data-testid="merchant-billing-banner"
      className={`px-6 py-3 flex items-center justify-between gap-4 border-b ${
        tone === "red"
          ? "bg-[#fde0e0] border-[#f0b0b0] text-[#a01010]"
          : "bg-[#fff4d6] border-[#f0d890] text-[#7a5a00]"
      }`}
    >
      <div className="flex items-center gap-3 text-sm font-semibold">
        <Icon className="w-4 h-4 flex-shrink-0" />
        {message}
      </div>
      <Button
        size="sm"
        onClick={goSettings}
        data-testid="banner-billing-cta"
        className="rounded-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white text-xs whitespace-nowrap"
      >
        {isPastDue ? "Pay now" : inGrace ? "Renew now" : "Manage billing"}
      </Button>
    </div>
  );
}

function BillingCard({ billing, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [autoRenew, setAutoRenew] = useState(!!billing.auto_renew_enabled);
  const [savingAr, setSavingAr] = useState(false);

  useEffect(() => { setAutoRenew(!!billing.auto_renew_enabled); }, [billing.auto_renew_enabled]);

  const statusMeta = {
    trialing: { label: "Free trial", cls: "bg-[#fff4d6] text-[#7a5a00]" },
    active: { label: "Active", cls: "bg-[#dcf6e2] text-[#1a6b34]" },
    past_due: { label: "Past due", cls: "bg-[#fde0e0] text-[#a01010]" },
    manual_bypass: { label: "Comped", cls: "bg-[#e6e6ff] text-[#1a1a8a]" },
    canceled: { label: "Canceled", cls: "bg-[#eeeeee] text-[#5a5a65]" },
  }[billing.status] || { label: billing.status, cls: "bg-[#eeeeee]" };

  const startCheckout = async (autoRenewChoice) => {
    setBusy(true);
    try {
      const { data } = await api.post("/business/billing/checkout", {
        origin_url: window.location.origin,
        auto_renew: !!autoRenewChoice,
      });
      try {
        window.sessionStorage.setItem("sparkloop-checkout-url", data.url);
      } catch { /* ignore quota */ }
      window.location.href = "/merchant/billing/confirm";
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const toggleAutoRenew = async (next) => {
    setSavingAr(true);
    try {
      await api.post("/business/billing/auto-renew", { auto_renew: next });
      setAutoRenew(next);
      toast.success(
        next
          ? "Auto-renew enabled — next charge will be automatic"
          : (billing.has_stripe_subscription
              ? "Auto-renew turned off — your current period stays active, you won't be charged again"
              : "Auto-renew turned off"),
      );
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAr(false);
    }
  };

  const isAnnual = billing.billing_period_days >= 180;
  const chargeAmount = isAnnual ? billing.monthly_price_usd * 12 : billing.monthly_price_usd;

  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-6 mb-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 flex items-center gap-2">
            <CreditCard className="w-3.5 h-3.5" />
            Billing · {billing.plan_label || (isAnnual ? "Annual" : "Monthly")}
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="merchant-billing-status"
              className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${statusMeta.cls}`}
            >
              {statusMeta.label}
            </span>
            <span className="font-display text-2xl font-extrabold">
              ${billing.monthly_price_usd.toFixed(2)}
              <span className="text-sm font-normal text-[#5a5a65]"> /mo</span>
            </span>
          </div>
          <div className="text-xs text-[#5a5a65] mt-2">
            {billing.status === "manual_bypass" ? (
              <>Paid offline — managed by your SparkLoop admin.</>
            ) : (
              <>
                {isAnnual && (
                  <span className="block">${chargeAmount.toFixed(2)} billed yearly</span>
                )}
                {billing.current_period_end && (
                  <span>
                    {billing.status === "trialing" ? "Trial ends" : (autoRenew && billing.has_stripe_subscription ? "Next auto-charge" : "Period ends")}:{" "}
                    <span className="font-mono-data">
                      {new Date(billing.current_period_end).toLocaleDateString()}
                    </span>
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {billing.status !== "manual_bypass" && (
          <Button
            data-testid="start-billing-checkout-btn"
            onClick={() => startCheckout(autoRenew)}
            disabled={busy}
            className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white"
          >
            {busy
              ? "Redirecting…"
              : billing.status === "past_due"
              ? (autoRenew ? "Pay & enable auto-renew" : "Pay now")
              : (autoRenew ? `Subscribe · $${chargeAmount.toFixed(2)}` : `Pay $${chargeAmount.toFixed(2)} once`)}
          </Button>
        )}
      </div>

      {billing.status !== "manual_bypass" && (
        <div className="mt-5 pt-5 border-t border-[#e5e5eb]">
          <label className="flex items-start justify-between gap-4 cursor-pointer" data-testid="auto-renew-toggle-row">
            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                Auto-renew subscription
                {autoRenew && billing.has_stripe_subscription && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-[#dcf6e2] text-[#1a6b34]">
                    <CheckCircle2 className="w-2.5 h-2.5" /> Active
                  </span>
                )}
              </div>
              <div className="text-xs text-[#5a5a65] mt-1 leading-relaxed max-w-md">
                {autoRenew
                  ? "Stripe will automatically charge your card at the end of each period. You can cancel any time — your current period stays active."
                  : "You'll need to manually pay each period. We'll show you a banner 7 days before it ends."}
              </div>
            </div>
            <button
              type="button"
              data-testid="auto-renew-toggle"
              onClick={() => toggleAutoRenew(!autoRenew)}
              disabled={savingAr}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors mt-0.5 ${
                autoRenew ? "bg-[#002fa7]" : "bg-[#d4d4dc]"
              } disabled:opacity-50`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  autoRenew ? "translate-x-5" : "translate-x-0.5"
                } translate-y-0.5`}
              />
            </button>
          </label>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-1">{label}</div>
      <div className={mono ? "font-mono-data text-sm break-all" : "font-semibold"}>{value}</div>
    </div>
  );
}

function EditablePointsRow({ business }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(business.points_per_dollar ?? 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(String(business.points_per_dollar ?? 1)); }, [business.points_per_dollar]);

  const save = async () => {
    const n = Number(value);
    if (!n || n <= 0) { toast.error("Enter a positive number"); return; }
    setSaving(true);
    try {
      await api.patch("/business/me/settings", { points_per_dollar: n });
      toast.success("Updated — refresh to see it everywhere");
      setEditing(false);
      // Reload so the rest of the app picks up the new rate.
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-1">Points per $1</div>
      {editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            data-testid="settings-ppd-input"
            type="number" step="0.01" min="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-10 w-28 rounded-xl border-[#e5e5eb] font-mono-data text-center"
            autoFocus
          />
          <span className="text-sm text-[#5a5a65]">pts per $1</span>
          <Button
            data-testid="settings-ppd-save"
            onClick={save}
            disabled={saving}
            size="sm"
            className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white h-8 px-4"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setEditing(false); setValue(String(business.points_per_dollar ?? 1)); }}
            size="sm"
            className="rounded-full h-8 px-3"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="font-semibold font-mono-data">{business.points_per_dollar} pts per $1</div>
          <button
            data-testid="settings-ppd-edit"
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs uppercase tracking-wider font-bold text-[#002fa7] hover:underline"
          >
            Edit
          </button>
        </div>
      )}
      <p className="text-[11px] text-[#5a5a65] mt-1">
        Set to <span className="font-mono-data">1</span> for 1 point per $1, or <span className="font-mono-data">10</span> for 10 points per $1.
      </p>
    </div>
  );
}


function Label({ children }) {
  return <label className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 block">{children}</label>;
}
